// v10/src/components/pilot/PilotTurnos.js
// v1.1 — Fix i18n: SLOT_TYPE_LABELS movido a función getSlotTypeLabels(t) para evitar t() a nivel de módulo
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar, Clock, Plane, User, X, ArrowLeft, Loader,
  CheckCircle, AlertCircle, XCircle, FileText, Send,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../../services/apiService';

const getSlotTypeLabels = (t) => ({
  instruction: t('slot_instruction',{ns:'admin'}),
  checkride: 'Checkride',
  solo: t('slot_solo',{ns:'admin'}),
});

const SLOT_TYPE_COLORS = {
  instruction: { bg: 'rgba(37,99,235,0.1)', dot: '#2563eb' },
  checkride:   { bg: 'rgba(124,58,237,0.1)', dot: '#7c3aed' },
  solo:        { bg: 'rgba(22,163,74,0.1)',   dot: '#16a34a' },
};
const STATUS_LABELS = { pending: 'Pendiente', confirmed: 'Confirmado', cancelled: 'Cancelado', completed: 'Completado' };
const STATUS_COLORS = {
  pending:   { bg: 'rgba(245,158,11,0.1)', border: 'rgba(245,158,11,0.3)', text: '#f59e0b', icon: Clock, gradient: 'linear-gradient(135deg,#f59e0b,#f97316)' },
  confirmed: { bg: 'rgba(34,197,94,0.1)',  border: 'rgba(34,197,94,0.3)',  text: '#22c55e', icon: CheckCircle, gradient: 'linear-gradient(135deg,#22c55e,#16a34a)' },
  cancelled: { bg: 'rgba(239,68,68,0.1)',  border: 'rgba(239,68,68,0.3)',  text: '#ef4444', icon: XCircle, gradient: 'linear-gradient(135deg,#ef4444,#dc2626)' },
  completed: { bg: 'rgba(99,102,241,0.1)', border: 'rgba(99,102,241,0.3)', text: '#6366f1', icon: CheckCircle, gradient: 'linear-gradient(135deg,#6366f1,#8b5cf6)' },
};

const formatDate = (d) => {
  if (!d) return '—';
  const parts = String(d).split('T')[0].split('-');
  if (parts.length !== 3) return String(d);
  const date = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
  return date.toLocaleDateString('es-AR', { weekday: 'short', day: 'numeric', month: 'short' });
};
const formatTime = (t) => t ? t.substring(0, 5) : '';

const staggerContainer = { hidden: {}, visible: { transition: { staggerChildren: 0.06 } } };
const staggerItem = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.3 } } };

const PilotTurnos = ({ darkMode, currentUser, onClose }) => {
  const { t } = useTranslation();
  const SLOT_TYPE_LABELS = getSlotTypeLabels(t);

  const [tab, setTab]             = useState('available');
  const [slots, setSlots]         = useState([]);
  const [bookings, setBookings]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [bookingSlot, setBookingSlot] = useState(null);
  const [pilotNotes, setPilotNotes]   = useState('');
  const [cancelModal, setCancelModal] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');
  const [success, setSuccess]     = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [selectedInstructor, setSelectedInstructor] = useState(null);

  const text   = darkMode ? 'white' : '#0f172a';
  const sub    = darkMode ? 'rgba(255,255,255,0.5)' : '#64748b';
  const cardBg = darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.7)';
  const border = darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)';
  const glassBg = darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.6)';

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = dateFilter ? `?date=${dateFilter}` : '';
      const [slotsRes, bookingsRes] = await Promise.all([
        api.get(`/api/slots/available${params}`),
        api.get('/api/bookings'),
      ]);
      setSlots(slotsRes.data.data || []);
      setBookings(bookingsRes.data.data || []);
    } catch (err) {
      console.error('Error fetching turnos:', err);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60 * 1000);
    return () => clearInterval(interval);
  }, [dateFilter]);

  const clearMsg = () => { setTimeout(() => { setError(''); setSuccess(''); }, 3000); };

  const handleBook = async () => {
    if (!bookingSlot) return;
    setSaving(true);
    try {
      await api.post('/api/bookings', { slot_id: bookingSlot.id, pilot_notes: pilotNotes || null });
      setBookingSlot(null); setPilotNotes('');
      setSuccess('Turno reservado. El instructor será notificado.'); clearMsg();
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Error al reservar'); clearMsg();
    } finally { setSaving(false); }
  };

  const handleCancel = async () => {
    if (!cancelModal) return;
    try {
      await api.put(`/api/bookings/${cancelModal}/cancel`, { reason: cancelReason });
      setCancelModal(null); setCancelReason('');
      setSuccess('Turno cancelado'); clearMsg();
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Error al cancelar'); clearMsg();
    }
  };

  // Group slots by instructor
  const slotsByInstructor = slots.reduce((acc, s) => {
    const key = s.instructor_id;
    if (!acc[key]) acc[key] = { name: s.instructor_name, email: s.instructor_email, slots: [] };
    acc[key].slots.push(s);
    return acc;
  }, {});

  // Group selected instructor's slots by date
  const selectedSlotsByDate = selectedInstructor
    ? (slotsByInstructor[selectedInstructor]?.slots || []).reduce((acc, s) => {
        const dateKey = s.date ? s.date.split('T')[0] : 'unknown';
        if (!acc[dateKey]) acc[dateKey] = [];
        acc[dateKey].push(s);
        return acc;
      }, {})
    : {};

  const myBookings = bookings;
  const activeBookings = myBookings.filter(b => b.status === 'pending' || b.status === 'confirmed');
  const pastBookings   = myBookings.filter(b => b.status === 'cancelled' || b.status === 'completed');

  const inp = {
    width: '100%', padding: '12px 14px', marginTop: 6, boxSizing: 'border-box',
    background: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.02)',
    border: darkMode ? '1.5px solid rgba(255,255,255,0.1)' : '1.5px solid rgba(0,0,0,0.08)',
    borderRadius: 12, fontSize: 14, color: text, outline: 'none', fontFamily: 'inherit',
    backdropFilter: 'blur(8px)',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 40 }}
      style={{ position: 'fixed', inset: 0, zIndex: 100, background: darkMode ? '#0a0f1e' : '#f1f5f9', overflowY: 'auto' }}>

      {/* Header */}
      <div style={{ position: 'sticky', top: 0, zIndex: 10,
        background: darkMode ? 'rgba(10,15,30,0.85)' : 'rgba(241,245,249,0.85)',
        backdropFilter: 'blur(20px)', borderBottom: border, padding: '14px 20px',
        display: 'flex', alignItems: 'center', gap: 14 }}>
        <motion.button onClick={onClose} whileTap={{ scale: 0.92 }}
          style={{ width: 40, height: 40, borderRadius: 12, border, background: glassBg, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', color: sub, backdropFilter: 'blur(8px)' }}>
          <ArrowLeft style={{ width: 18, height: 18 }} />
        </motion.button>
        <div style={{ flex: 1 }}>
          <p style={{ margin: 0, fontSize: 18, fontWeight: 800, color: text, display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 32, height: 32, borderRadius: 10, background: 'linear-gradient(135deg,#3b82f6,#6366f1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 3px 12px rgba(59,130,246,0.3)' }}>
              <Calendar style={{ width: 16, height: 16, color: 'white' }} />
            </div>
            Mis Turnos
          </p>
          <p style={{ margin: '2px 0 0 40px', fontSize: 12, color: sub }}>Reservá turnos de instrucción</p>
        </div>
      </div>

      <div style={{ maxWidth: 640, margin: '0 auto', padding: '24px 16px' }}>

        {/* Messages */}
        <AnimatePresence>
          {(error || success) && (
            <motion.div initial={{ opacity: 0, y: -8, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, scale: 0.98 }}
              style={{ marginBottom: 16, padding: '12px 16px', borderRadius: 16, display: 'flex', alignItems: 'center', gap: 10,
                background: error ? 'rgba(239,68,68,0.08)' : 'rgba(34,197,94,0.08)',
                border: `1px solid ${error ? 'rgba(239,68,68,0.2)' : 'rgba(34,197,94,0.2)'}`,
                backdropFilter: 'blur(12px)' }}>
              {error ? <AlertCircle style={{ width: 16, height: 16, color: '#ef4444' }} /> : <CheckCircle style={{ width: 16, height: 16, color: '#22c55e' }} />}
              <span style={{ fontSize: 13, fontWeight: 600, color: error ? '#ef4444' : '#22c55e' }}>{error || success}</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 24, background: glassBg, borderRadius: 16, padding: 5, border, backdropFilter: 'blur(12px)' }}>
          {[
            { id: 'available', label: 'Disponibles', count: slots.length },
            { id: 'mine', label: 'Mis turnos', count: activeBookings.length },
            { id: 'history', label: 'Historial', count: pastBookings.length },
          ].map(t => (
            <motion.button key={t.id} onClick={() => { setTab(t.id); if (t.id !== 'available') setSelectedInstructor(null); }}
              whileTap={{ scale: 0.97 }}
              style={{ flex: 1, padding: '10px 12px', borderRadius: 12, border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                fontSize: 13, fontWeight: 700, transition: 'all 0.2s ease',
                background: tab === t.id ? 'linear-gradient(135deg,#3b82f6,#6366f1)' : 'transparent',
                color: tab === t.id ? 'white' : sub,
                boxShadow: tab === t.id ? '0 4px 12px rgba(59,130,246,0.25)' : 'none' }}>
              {t.label}
              {t.count > 0 && <span style={{ marginLeft: 5, fontSize: 10, fontWeight: 800, padding: '2px 6px', borderRadius: 6,
                background: tab === t.id ? 'rgba(255,255,255,0.25)' : 'rgba(59,130,246,0.12)', color: tab === t.id ? 'white' : '#3b82f6' }}>{t.count}</span>}
            </motion.button>
          ))}
        </div>

        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 60, gap: 12 }}>
            <Loader style={{ width: 28, height: 28, animation: 'spin 1s linear infinite', color: '#3b82f6' }} />
            <span style={{ fontSize: 13, color: sub }}>Cargando turnos...</span>
          </div>
        ) : (
          <>
            {/* Available slots */}
            {tab === 'available' && (
              <div>
                {/* Date filter */}
                <div style={{ marginBottom: 20, borderRadius: 16, border, background: glassBg, padding: '14px 16px', backdropFilter: 'blur(12px)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Calendar style={{ width: 14, height: 14, color: '#3b82f6' }} />
                    <label style={{ fontSize: 12, fontWeight: 700, color: sub, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Filtrar por fecha</label>
                  </div>
                  <input type="date" value={dateFilter} onChange={e => setDateFilter(e.target.value)} style={inp} />
                  {dateFilter && (
                    <motion.button onClick={() => setDateFilter('')} whileTap={{ scale: 0.95 }}
                      style={{ marginTop: 8, fontSize: 12, fontWeight: 700, color: '#3b82f6', background: 'rgba(59,130,246,0.1)',
                        border: 'none', cursor: 'pointer', padding: '5px 12px', borderRadius: 8 }}>
                      Ver todos
                    </motion.button>
                  )}
                </div>

                {Object.keys(slotsByInstructor).length === 0 && (
                  <div style={{ textAlign: 'center', padding: '50px 20px', borderRadius: 20, border, background: glassBg, backdropFilter: 'blur(12px)' }}>
                    <Calendar style={{ width: 40, height: 40, color: sub, marginBottom: 12, opacity: 0.4 }} />
                    <p style={{ color: sub, fontSize: 14, fontWeight: 600, margin: 0 }}>No hay slots disponibles</p>
                    <p style={{ color: sub, fontSize: 12, margin: '6px 0 0', opacity: 0.7 }}>Probá con otra fecha</p>
                  </div>
                )}

                {/* Instructor list */}
                {!selectedInstructor && (
                  <motion.div variants={staggerContainer} initial="hidden" animate="visible"
                    style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {Object.entries(slotsByInstructor).map(([instructorId, data]) => (
                      <motion.div key={instructorId} variants={staggerItem}>
                        <motion.button
                          onClick={() => setSelectedInstructor(instructorId)}
                          whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.98 }}
                          style={{ width: '100%', textAlign: 'left', cursor: 'pointer', padding: '16px 18px', borderRadius: 18,
                            border, background: cardBg, backdropFilter: 'blur(12px)', fontFamily: 'inherit',
                            display: 'flex', alignItems: 'center', gap: 14,
                            transition: 'box-shadow 0.2s ease',
                            boxShadow: darkMode ? '0 2px 12px rgba(0,0,0,0.2)' : '0 2px 12px rgba(0,0,0,0.04)' }}>
                          {/* Instructor avatar */}
                          <div style={{ width: 48, height: 48, borderRadius: '50%', flexShrink: 0,
                            background: 'linear-gradient(135deg,#3b82f6,#6366f1)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            boxShadow: '0 4px 12px rgba(59,130,246,0.25)',
                            fontSize: 20, fontWeight: 800, color: 'white' }}>
                            {data.name ? data.name.charAt(0).toUpperCase() : '?'}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: text }}>{data.name}</p>
                            <p style={{ margin: '2px 0 0', fontSize: 12, color: sub }}>{data.email || ''}</p>
                          </div>
                          <div style={{ textAlign: 'right', flexShrink: 0 }}>
                            <div style={{ padding: '6px 14px', borderRadius: 10,
                              background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)' }}>
                              <span style={{ fontSize: 18, fontWeight: 800, color: '#3b82f6' }}>{data.slots.length}</span>
                              <p style={{ margin: 0, fontSize: 10, fontWeight: 600, color: sub }}>
                                {data.slots.length === 1 ? 'slot' : 'slots'}
                              </p>
                            </div>
                          </div>
                        </motion.button>
                      </motion.div>
                    ))}
                  </motion.div>
                )}

                {/* Selected instructor slots */}
                {selectedInstructor && slotsByInstructor[selectedInstructor] && (
                  <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.25 }}>
                    {/* Back to instructors */}
                    <motion.button onClick={() => setSelectedInstructor(null)} whileTap={{ scale: 0.95 }}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16, padding: '6px 12px',
                        borderRadius: 10, border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                        fontSize: 13, fontWeight: 700, color: '#3b82f6', background: 'rgba(59,130,246,0.1)' }}>
                      <ArrowLeft style={{ width: 14, height: 14 }} /> Todos los instructores
                    </motion.button>

                    {/* Instructor header */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, padding: '16px 18px',
                      borderRadius: 18, border, background: cardBg, backdropFilter: 'blur(12px)' }}>
                      <div style={{ width: 44, height: 44, borderRadius: '50%',
                        background: 'linear-gradient(135deg,#3b82f6,#6366f1)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 18, fontWeight: 800, color: 'white' }}>
                        {slotsByInstructor[selectedInstructor].name?.charAt(0).toUpperCase() || '?'}
                      </div>
                      <div>
                        <p style={{ margin: 0, fontSize: 16, fontWeight: 800, color: text }}>{slotsByInstructor[selectedInstructor].name}</p>
                        <p style={{ margin: '2px 0 0', fontSize: 12, color: sub }}>
                          {slotsByInstructor[selectedInstructor].slots.length} horarios disponibles
                        </p>
                      </div>
                    </div>

                    {/* Slots grouped by date as chips */}
                    {Object.entries(selectedSlotsByDate).sort().map(([dateKey, dateSlots]) => (
                      <div key={dateKey} style={{ marginBottom: 16 }}>
                        <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 700, color: sub, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          {formatDate(dateKey)}
                        </p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                          {dateSlots.map(slot => {
                            const stc = SLOT_TYPE_COLORS[slot.slot_type] || SLOT_TYPE_COLORS.instruction;
                            return (
                              <motion.button key={slot.id}
                                onClick={() => setBookingSlot(slot)}
                                whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.95 }}
                                style={{ padding: '10px 16px', borderRadius: 14, border, cursor: 'pointer', fontFamily: 'inherit',
                                  background: cardBg, backdropFilter: 'blur(12px)',
                                  display: 'flex', alignItems: 'center', gap: 10,
                                  transition: 'box-shadow 0.2s',
                                  boxShadow: darkMode ? '0 2px 8px rgba(0,0,0,0.2)' : '0 2px 8px rgba(0,0,0,0.04)' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
                                  <span style={{ fontSize: 14, fontWeight: 800, color: text, display: 'flex', alignItems: 'center', gap: 5 }}>
                                    <Clock style={{ width: 12, height: 12, color: stc.dot }} />
                                    {formatTime(slot.start_time)} - {formatTime(slot.end_time)}
                                  </span>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 11, fontWeight: 600, color: stc.dot }}>
                                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: stc.dot }} />
                                      {SLOT_TYPE_LABELS[slot.slot_type]}
                                    </span>
                                    {slot.aircraft_name && (
                                      <span style={{ fontSize: 10, color: sub, display: 'flex', alignItems: 'center', gap: 3 }}>
                                        <Plane style={{ width: 10, height: 10 }} /> {slot.aircraft_name}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div style={{ width: 28, height: 28, borderRadius: 8,
                                  background: 'linear-gradient(135deg,#22c55e,#16a34a)',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                  boxShadow: '0 2px 8px rgba(34,197,94,0.25)' }}>
                                  <Send style={{ width: 12, height: 12, color: 'white' }} />
                                </div>
                              </motion.button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </motion.div>
                )}
              </div>
            )}

            {/* My active bookings */}
            {tab === 'mine' && (
              <motion.div variants={staggerContainer} initial="hidden" animate="visible"
                style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {activeBookings.length === 0 && (
                  <motion.div variants={staggerItem}
                    style={{ textAlign: 'center', padding: '50px 20px', borderRadius: 20, border, background: glassBg, backdropFilter: 'blur(12px)' }}>
                    <Calendar style={{ width: 40, height: 40, color: sub, marginBottom: 12, opacity: 0.4 }} />
                    <p style={{ color: sub, fontSize: 14, fontWeight: 600, margin: 0 }}>No tenés turnos activos</p>
                    <p style={{ color: sub, fontSize: 12, margin: '6px 0 0', opacity: 0.7 }}>Explorá los slots disponibles para reservar</p>
                  </motion.div>
                )}
                {activeBookings.map(b => {
                  const sc = STATUS_COLORS[b.status] || STATUS_COLORS.pending;
                  const Icon = sc.icon;
                  return (
                    <motion.div key={b.id} variants={staggerItem}
                      style={{ borderRadius: 20, border, background: cardBg, overflow: 'hidden',
                        backdropFilter: 'blur(12px)', position: 'relative',
                        boxShadow: darkMode ? '0 2px 12px rgba(0,0,0,0.2)' : '0 2px 12px rgba(0,0,0,0.04)' }}>
                      {/* Status stripe */}
                      <div style={{ height: 3, background: sc.gradient }} />
                      <div style={{ padding: '16px 18px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                          <span style={{ padding: '4px 12px', borderRadius: 10, fontSize: 11, fontWeight: 700,
                            background: sc.bg, border: `1px solid ${sc.border}`, color: sc.text,
                            display: 'flex', alignItems: 'center', gap: 5 }}>
                            <Icon style={{ width: 12, height: 12 }} /> {STATUS_LABELS[b.status]}
                          </span>
                          <span style={{ fontSize: 11, fontWeight: 600, color: sub, padding: '3px 8px', borderRadius: 6,
                            background: darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)' }}>
                            {SLOT_TYPE_LABELS[b.slot_type] || b.slot_type}
                          </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 6, flexWrap: 'wrap' }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 14, fontWeight: 700, color: text }}>
                            <Calendar style={{ width: 14, height: 14, color: '#3b82f6' }} />
                            {b.date ? formatDate(b.date) : '—'}
                          </span>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, color: sub }}>
                            <Clock style={{ width: 13, height: 13 }} />
                            {formatTime(b.start_time)} - {formatTime(b.end_time)}
                          </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                          <div style={{ width: 24, height: 24, borderRadius: '50%',
                            background: 'linear-gradient(135deg,#3b82f6,#6366f1)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 11, fontWeight: 800, color: 'white' }}>
                            {b.instructor_name ? b.instructor_name.charAt(0).toUpperCase() : '?'}
                          </div>
                          <span style={{ fontSize: 13, color: text, fontWeight: 600 }}>
                            {b.instructor_name || '—'}
                          </span>
                        </div>
                        {b.aircraft_name && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                            <Plane style={{ width: 13, height: 13, color: sub }} />
                            <span style={{ fontSize: 12, color: sub }}>{b.aircraft_name} {b.registration ? `(${b.registration})` : ''}</span>
                          </div>
                        )}
                        {b.pilot_notes && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, padding: '8px 12px',
                            borderRadius: 10, background: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)' }}>
                            <FileText style={{ width: 12, height: 12, color: sub, flexShrink: 0 }} />
                            <span style={{ fontSize: 12, color: sub, fontStyle: 'italic' }}>{b.pilot_notes}</span>
                          </div>
                        )}
                        <div style={{ marginTop: 12 }}>
                          <motion.button onClick={() => setCancelModal(b.id)} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.95 }}
                            style={{ padding: '8px 16px', borderRadius: 12, border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                              fontSize: 12, fontWeight: 700, background: 'rgba(239,68,68,0.1)', color: '#ef4444',
                              display: 'flex', alignItems: 'center', gap: 5 }}>
                            <XCircle style={{ width: 13, height: 13 }} /> Cancelar turno
                          </motion.button>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </motion.div>
            )}

            {/* History */}
            {tab === 'history' && (
              <motion.div variants={staggerContainer} initial="hidden" animate="visible"
                style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {pastBookings.length === 0 && (
                  <motion.div variants={staggerItem}
                    style={{ textAlign: 'center', padding: '50px 20px', borderRadius: 20, border, background: glassBg, backdropFilter: 'blur(12px)' }}>
                    <FileText style={{ width: 40, height: 40, color: sub, marginBottom: 12, opacity: 0.4 }} />
                    <p style={{ color: sub, fontSize: 14, fontWeight: 600, margin: 0 }}>Sin historial de turnos</p>
                  </motion.div>
                )}
                {pastBookings.map(b => {
                  const sc = STATUS_COLORS[b.status] || STATUS_COLORS.pending;
                  const Icon = sc.icon;
                  return (
                    <motion.div key={b.id} variants={staggerItem}
                      style={{ borderRadius: 16, border, background: cardBg, overflow: 'hidden',
                        backdropFilter: 'blur(12px)', opacity: 0.7 }}>
                      <div style={{ height: 2, background: sc.gradient }} />
                      <div style={{ padding: '14px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                          <span style={{ padding: '3px 10px', borderRadius: 8, fontSize: 10, fontWeight: 700,
                            background: sc.bg, border: `1px solid ${sc.border}`, color: sc.text,
                            display: 'flex', alignItems: 'center', gap: 4 }}>
                            <Icon style={{ width: 10, height: 10 }} /> {STATUS_LABELS[b.status]}
                          </span>
                          <span style={{ fontSize: 13, fontWeight: 600, color: sub }}>{b.date ? formatDate(b.date) : '—'}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: sub }}>
                          <div style={{ width: 20, height: 20, borderRadius: '50%',
                            background: 'linear-gradient(135deg,#3b82f6,#6366f1)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 9, fontWeight: 800, color: 'white' }}>
                            {b.instructor_name ? b.instructor_name.charAt(0).toUpperCase() : '?'}
                          </div>
                          {b.instructor_name || '—'}
                          {b.aircraft_name ? ` · ${b.aircraft_name}` : ''}
                        </div>
                        {b.cancelled_reason && (
                          <p style={{ margin: '6px 0 0', fontSize: 11, color: '#ef4444', padding: '4px 8px',
                            borderRadius: 6, background: 'rgba(239,68,68,0.06)' }}>
                            Motivo: {b.cancelled_reason}
                          </p>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </motion.div>
            )}
          </>
        )}
      </div>

      {/* Modal reservar */}
      <AnimatePresence>
        {bookingSlot && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: 'fixed', inset: 0, zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: 16, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(8px)' }}
            onClick={(e) => { if (e.target === e.currentTarget) setBookingSlot(null); }}>
            <motion.div initial={{ scale: 0.9, y: 20, opacity: 0 }} animate={{ scale: 1, y: 0, opacity: 1 }} exit={{ scale: 0.9, y: 20, opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              style={{ width: '100%', maxWidth: 420, borderRadius: 24, overflow: 'hidden',
                background: darkMode ? 'rgba(17,24,39,0.95)' : 'rgba(255,255,255,0.95)',
                backdropFilter: 'blur(20px)', border,
                boxShadow: '0 24px 60px rgba(0,0,0,0.3)' }}>
              <div style={{ height: 4, background: 'linear-gradient(90deg,#22c55e,#16a34a)' }} />
              <div style={{ padding: 28 }}>
                <h3 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 800, color: text, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 34, height: 34, borderRadius: 10, background: 'linear-gradient(135deg,#22c55e,#16a34a)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(34,197,94,0.25)' }}>
                    <Send style={{ width: 16, height: 16, color: 'white' }} />
                  </div>
                  Reservar turno
                </h3>

                {/* Summary card */}
                <div style={{ borderRadius: 16, border, background: glassBg, padding: '16px 18px', marginBottom: 20, backdropFilter: 'blur(8px)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 10 }}>
                    <div style={{ width: 36, height: 36, borderRadius: '50%',
                      background: 'linear-gradient(135deg,#3b82f6,#6366f1)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 15, fontWeight: 800, color: 'white' }}>
                      {bookingSlot.instructor_name?.charAt(0).toUpperCase() || '?'}
                    </div>
                    <div>
                      <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: text }}>{bookingSlot.instructor_name}</p>
                      <p style={{ margin: 0, fontSize: 12, color: sub }}>
                        {SLOT_TYPE_LABELS[bookingSlot.slot_type] || bookingSlot.slot_type}
                      </p>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 14, fontWeight: 700, color: text,
                      padding: '6px 12px', borderRadius: 10, background: darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)' }}>
                      <Calendar style={{ width: 13, height: 13, color: '#3b82f6' }} />
                      {formatDate(bookingSlot.date)}
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 14, fontWeight: 700, color: text,
                      padding: '6px 12px', borderRadius: 10, background: darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)' }}>
                      <Clock style={{ width: 13, height: 13, color: sub }} />
                      {formatTime(bookingSlot.start_time)} - {formatTime(bookingSlot.end_time)}
                    </span>
                  </div>
                  {bookingSlot.aircraft_name && (
                    <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: sub }}>
                      <Plane style={{ width: 12, height: 12 }} /> {bookingSlot.aircraft_name}
                      {bookingSlot.registration ? ` (${bookingSlot.registration})` : ''}
                    </div>
                  )}
                </div>

                <label style={{ display: 'block', marginBottom: 24 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: sub, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Notas para el instructor</span>
                  <textarea value={pilotNotes} onChange={e => setPilotNotes(e.target.value)}
                    rows={3} style={{ ...inp, resize: 'vertical' }} placeholder="Opcional — ej: practicar emergencias, primera vez en el avión" />
                </label>

                <div style={{ display: 'flex', gap: 10 }}>
                  <motion.button onClick={() => { setBookingSlot(null); setPilotNotes(''); }} whileTap={{ scale: 0.97 }}
                    style={{ flex: 1, padding: 14, borderRadius: 14, border, background: 'transparent', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, fontWeight: 600, color: sub }}>
                    Cancelar
                  </motion.button>
                  <motion.button onClick={handleBook} disabled={saving} whileTap={{ scale: 0.97 }}
                    style={{ flex: 1, padding: 14, borderRadius: 14, border: 'none', cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
                      fontSize: 14, fontWeight: 700, background: 'linear-gradient(135deg,#22c55e,#16a34a)', color: 'white',
                      boxShadow: '0 4px 16px rgba(34,197,94,0.3)', opacity: saving ? 0.7 : 1 }}>
                    {saving ? 'Reservando...' : 'Confirmar reserva'}
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal cancelar */}
      <AnimatePresence>
        {cancelModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: 'fixed', inset: 0, zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: 16, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(8px)' }}
            onClick={(e) => { if (e.target === e.currentTarget) setCancelModal(null); }}>
            <motion.div initial={{ scale: 0.9, y: 20, opacity: 0 }} animate={{ scale: 1, y: 0, opacity: 1 }} exit={{ scale: 0.9, y: 20, opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              style={{ width: '100%', maxWidth: 400, borderRadius: 24, overflow: 'hidden',
                background: darkMode ? 'rgba(17,24,39,0.95)' : 'rgba(255,255,255,0.95)',
                backdropFilter: 'blur(20px)', border,
                boxShadow: '0 24px 60px rgba(0,0,0,0.3)' }}>
              <div style={{ height: 4, background: 'linear-gradient(90deg,#ef4444,#f97316)' }} />
              <div style={{ padding: 28 }}>
                <h3 style={{ margin: '0 0 6px', fontSize: 18, fontWeight: 800, color: text, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <XCircle style={{ width: 20, height: 20, color: '#ef4444' }} /> Cancelar turno
                </h3>
                <p style={{ margin: '0 0 16px', fontSize: 13, color: sub }}>El instructor será notificado de la cancelación</p>
                <textarea value={cancelReason} onChange={e => setCancelReason(e.target.value)}
                  rows={3} style={inp} placeholder="Motivo de la cancelación (opcional)" />
                <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                  <motion.button onClick={() => { setCancelModal(null); setCancelReason(''); }} whileTap={{ scale: 0.97 }}
                    style={{ flex: 1, padding: 14, borderRadius: 14, border, background: 'transparent', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, fontWeight: 600, color: sub }}>
                    Volver
                  </motion.button>
                  <motion.button onClick={handleCancel} whileTap={{ scale: 0.97 }}
                    style={{ flex: 1, padding: 14, borderRadius: 14, border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                      fontSize: 14, fontWeight: 700, background: 'linear-gradient(135deg,#ef4444,#dc2626)', color: 'white',
                      boxShadow: '0 4px 16px rgba(239,68,68,0.3)' }}>
                    Confirmar cancelación
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
    </motion.div>
  );
};

export default PilotTurnos;
